import React from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/nova/components';

import type { AssetMiniStats } from '../../useAssetLibraryStats';
import * as S from './AssetCardStats.styles';

/** Which step unlocks an asset's stats, for the hint shown while it has no tracking link. */
export type AssetCardStatsHint =
  /** Generated: get a tracking link for it. */
  | 'get-link'
  /** A video ad not rendered yet. */
  | 'render-video'
  /** Any other asset not generated yet. */
  | 'generate-first'
  /** A landing page: its own link, or an ad's link pointed at it. */
  | 'landing-page';

export interface AssetCardStatsProps {
  /** The library's stats read has settled for this brand. Before it, a skeleton holds the slot (an asset without an
   *  entry is "unknown", not "no link", so no hint). */
  loaded: boolean;
  /** The library's first read failed (nothing settled): the slot stays, empty. */
  failed?: boolean;
  /** The asset's figures; none (once loaded) = it has no tracking link yet. */
  stats?: AssetMiniStats;
  /** What unlocks the stats, for the no-link hint. */
  hint: AssetCardStatsHint;
  /** Lead with the asset's Views (a landing page): its page views over the library's window, from the analytics
   *  service. */
  withViews?: boolean;
  /** The Views figure: null when the analytics service cannot say (not set up here, or the read failed), a dash. */
  views?: number | null;
  /** The Views are being read (a skeleton holds the figure). */
  viewsLoading?: boolean;
  /** Open the asset's stats sheet. */
  onOpen: () => void;
}

/** The footer's figures while they load: the same slot and labels, a skeleton line for each figure. Also what a library
 *  grid's skeleton card shows while the list itself loads, so the card it becomes keeps the same height. */
export const AssetCardStatsSkeleton = ({ withViews = false }: { withViews?: boolean }): React.ReactElement => (
  <S.Foot aria-hidden="true">
    <S.StatsCells>
      {withViews ? (
        <S.Stat>
          <S.SkeletonValue>
            <Skeleton width={24} height="1em" />
          </S.SkeletonValue>
          <S.StatLabel text="Views" />
        </S.Stat>
      ) : null}
      <S.Stat>
        <S.SkeletonValue>
          <Skeleton width={24} height="1em" />
        </S.SkeletonValue>
        <S.StatLabel text="Reg" />
      </S.Stat>
      <S.Stat>
        <S.SkeletonValue>
          <Skeleton width={24} height="1em" />
        </S.SkeletonValue>
        <S.StatLabel text="FTD" />
      </S.Stat>
      <S.Stat>
        <S.SkeletonValue>
          <Skeleton width={32} height="1em" />
        </S.SkeletonValue>
        <S.StatLabel text="Reg → FTD" />
      </S.Stat>
    </S.StatsCells>
  </S.Foot>
);

/** "Reg -> FTD" as a whole percent, a dash before any registration. Pure. */
const regToFtd = (stats: AssetMiniStats): string =>
  stats.registrations > 0 ? `${Math.round((stats.ftds / stats.registrations) * 100)}%` : '–';

/**
 * A marketing library card's footer: the asset's tracking-link figures over the library's window (Reg / FTD /
 * Reg -> FTD, a landing page led by its Views), a button that opens its stats sheet; a quiet hint at the step that
 * unlocks them while it has no link; a skeleton of the figures until the library's read lands. Every state holds the
 * same slot.
 */
export const AssetCardStats = ({
  loaded,
  failed,
  stats,
  hint,
  withViews = false,
  views = null,
  viewsLoading = false,
  onOpen,
}: AssetCardStatsProps): React.ReactElement => {
  const { t } = useTranslation();
  if (!loaded && failed) return <S.Foot aria-hidden="true" />;
  if (!loaded) return <AssetCardStatsSkeleton withViews={withViews} />;
  if (stats) {
    return (
      <S.Foot>
        <S.StatsButton type="button" onClick={onOpen} aria-label={t('View performance')}>
          {withViews ? (
            <S.Stat title={views === null && !viewsLoading ? t('Views are not available right now') : undefined}>
              {viewsLoading ? (
                <S.SkeletonValue>
                  <Skeleton width={24} height="1em" />
                </S.SkeletonValue>
              ) : (
                <S.StatValue raw text={views === null ? '–' : String(views)} />
              )}
              <S.StatLabel text="Views" />
            </S.Stat>
          ) : null}
          <S.Stat>
            <S.StatValue raw text={String(stats.registrations)} />
            <S.StatLabel text="Reg" />
          </S.Stat>
          <S.Stat>
            <S.StatValue raw text={String(stats.ftds)} />
            <S.StatLabel text="FTD" />
          </S.Stat>
          <S.Stat>
            <S.StatValue raw text={regToFtd(stats)} />
            <S.StatLabel text="Reg → FTD" />
          </S.Stat>
        </S.StatsButton>
      </S.Foot>
    );
  }
  // No tracking link yet: the step that unlocks stats instead of a row of zeros (literal copy per case, so every line
  // is extracted). A landing page still has its Views (they need no link), so they lead the hint.
  const hintBlock = (
    <S.StatsHint>
      <S.StatsHintTitle text="Stats not available" />
      {hint === 'render-video' ? (
        <S.StatsHintText text="Generate the video to get a tracking link." />
      ) : hint === 'generate-first' ? (
        <S.StatsHintText text="Generate it first to get a tracking link." />
      ) : hint === 'landing-page' ? (
        <S.StatsHintText text="Get its tracking link, or point an ad's link here, to see performance." />
      ) : (
        <S.StatsHintText text="Generate a tracking link to see performance." />
      )}
    </S.StatsHint>
  );
  if (withViews && (viewsLoading || views !== null)) {
    return (
      <S.Foot>
        <S.HintRow>
          <S.Stat>
            {viewsLoading ? (
              <S.SkeletonValue>
                <Skeleton width={24} height="1em" />
              </S.SkeletonValue>
            ) : (
              <S.StatValue raw text={String(views)} />
            )}
            <S.StatLabel text="Views" />
          </S.Stat>
          {hintBlock}
        </S.HintRow>
      </S.Foot>
    );
  }
  return <S.Foot>{hintBlock}</S.Foot>;
};

export default AssetCardStats;
